import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUp,
  Sparkles,
  Square,
  SearchCode,
  Database,
  ListChecks,
  FolderTree,
  AlertTriangle,
  Send,
  CheckCircle2,
} from 'lucide-react';
import { useFeatureRequest, useFeatureRequestChat, useTransitionFeatureRequest, type ChatMessage } from '../../hooks/use-feature-requests';
import { useAuthStore } from '../../stores/auth-store';
import { useToast } from '../../components/ui/toast';
import {
  FEATURE_REQUEST_STATUS_LABELS,
  FEATURE_REQUEST_STATUS_STYLES,
  FEATURE_REQUEST_TYPE_LABELS,
  FEATURE_REQUEST_TYPE_STYLES,
} from '../../lib/feature-request-constants';
import { timeAgo } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';

// ─── Tool icon mapping ───────────────────────────────────────────────────────

function ToolIcon({ label }: { label: string }) {
  const l = label.toLowerCase();
  if (l.includes('cod')) return <SearchCode size={13} />;
  if (l.includes('baza de date')) return <Database size={13} />;
  if (l.includes('cereri')) return <ListChecks size={13} />;
  if (l.includes('structur')) return <FolderTree size={13} />;
  return <Sparkles size={13} />;
}

// ─── Spec card (renders the AI's final SPECIFICAȚIE block) ───────────────────

interface SpecFields {
  title?: string;
  problem?: string;
  solution?: string;
  alternatives?: string;
  impact?: string;
  risks?: string;
  effort?: string;
  recommendation?: string;
  reason?: string;
}

/** Clean a spec field: drop code fences and the trailing confirmation prompt. */
function cleanSpecValue(value: string | undefined): string | undefined {
  if (!value) return undefined;
  let v = value.replace(/```/g, '').trim();
  // The model often appends "E corectă descrierea? Răspunde «da»..." — that
  // belongs to the chat, not the spec card.
  v = v.replace(/\s*E corect[ăa] descrierea\?.*$/is, '').trim();
  return v || undefined;
}

/**
 * Map a diacritic letter to a character class covering its ASCII + diacritic
 * variants, so spec labels match regardless of which diacritic form the model
 * emitted (e.g. SOLUTIA_PROPUȘĂ / SOLUTIA_PROPUŞA / SOLUTIA PROPUSA).
 * Romanian has two comma/cedilla variants for S and T, so each class covers
 * both. Underscores also match a space, since the model occasionally uses either.
 * The regex is built case-insensitive, so classes only need lowercase forms.
 */
const DIACRITIC_CLASS: Record<string, string> = {
  'ă': '[ăa]',
  'â': '[âa]',
  'î': '[îi]',
  'ș': '[șşs]',
  'ş': '[șşs]',
  'ț': '[țţt]',
  'ţ': '[țţt]',
  '_': '[_ ]',
};

function diacriticInsensitive(label: string): string {
  return Array.from(label.toLowerCase())
    .map((ch) => DIACRITIC_CLASS[ch] ?? ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('');
}

function extractSpec(text: string): SpecFields | null {
  if (!text.includes('SPECIFICAȚIE') && !text.includes('SPECIFICATIE')) return null;
  const grab = (label: string): string | undefined => {
    const re = new RegExp(
      `${diacriticInsensitive(label)}:\\s*(.+?)(?=(?:\\n\\s*[A-ZĂÂÎȘȚ][A-ZĂÂÎȘȚ0-9_ ]{2,30}:)|$)`,
      'is',
    );
    const m = text.match(re);
    return m?.[1]?.trim() || undefined;
  };
  const title = cleanSpecValue(grab('TITLU'));
  if (!title) return null;
  return {
    title,
    problem: cleanSpecValue(grab('PROBLEMA')),
    solution: cleanSpecValue(grab('SOLUTIA_PROPUȘĂ')),
    alternatives: cleanSpecValue(grab('ALTERNATIVE_DISCUTATE')),
    impact: cleanSpecValue(grab('IMPACT')),
    risks: cleanSpecValue(grab('RISCURI')),
    effort: cleanSpecValue(grab('EFORT')),
    recommendation: cleanSpecValue(grab('RECOMANDARE')),
    reason: cleanSpecValue(grab('MOTIV_RECOMANDARE')),
  };
}

/**
 * Remove the raw SPECIFICAȚIE block from a message so the bubble doesn't
 * duplicate what the SpecCard already renders nicely below it.
 */
function stripSpecBlock(text: string): string {
  const idx = text.search(/SPECIFICA[ȚT]IE/);
  if (idx === -1) return text;
  return text.slice(0, idx).replace(/```/g, '').trim();
}

function SpecCard({ spec }: { spec: SpecFields }) {
  const approve = (spec.recommendation ?? '').toUpperCase().includes('APROB');
  const rows: Array<{ label: string; value?: string }> = [
    { label: 'Problema', value: spec.problem },
    { label: 'Soluția propusă', value: spec.solution },
    { label: 'Alternative discutate', value: spec.alternatives },
    { label: 'Impact', value: spec.impact },
    { label: 'Riscuri', value: spec.risks },
  ];
  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-[color:var(--surface-line)] bg-[var(--surface-panel)]">
      <div className="flex items-center justify-between border-b border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-primary" />
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-[color:var(--ink-strong)]">
            Specificație
          </span>
        </div>
        <span
          className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
            approve ? 'bg-success/14 text-success' : 'bg-error/12 text-error'
          }`}
        >
          {approve ? 'AI: APROBARE' : 'AI: RESPINGERE'}
        </span>
      </div>
      <div className="space-y-3 px-4 py-3.5">
        {rows.map((row) =>
          row.value ? (
            <div key={row.label}>
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">
                {row.label}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed text-[color:var(--ink-base)]">
                {row.value}
              </p>
            </div>
          ) : null,
        )}
        {spec.effort && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">
              Efort
            </span>
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold text-primary">
              {spec.effort}
            </span>
          </div>
        )}
        {spec.reason && (
          <div className="rounded-xl bg-[var(--surface-panel-strong)] px-3.5 py-3">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">
              Motiv
            </p>
            <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-[color:var(--ink-base)]">
              {spec.reason}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Message bubble ──────────────────────────────────────────────────────────

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-[color:var(--ink-soft)]"
          style={{ animationDelay: `${i * 140}ms` }}
        />
      ))}
    </span>
  );
}

function MessageBubble({
  message,
  isStreaming,
  userInitial,
}: {
  message: ChatMessage;
  isStreaming: boolean;
  userInitial: string;
}) {
  const isUser = message.role === 'user';
  const spec = !isUser ? extractSpec(message.content) : null;
  // When a spec card is rendered below, hide the raw SPECIFICAȚIE text from
  // the bubble so the content isn't shown twice.
  const bubbleText = spec ? stripSpecBlock(message.content) : message.content;
  const showThinking =
    !isUser && message.pending && !message.content && !message.toolEvents.length;

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <span
        className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isUser
            ? 'bg-primary/12 text-primary'
            : 'bg-gradient-to-br from-primary to-accent text-white shadow-[var(--shadow-soft)]'
        }`}
      >
        {isUser ? (
          <span className="text-[11px] font-bold">{userInitial}</span>
        ) : (
          <Sparkles size={14} />
        )}
      </span>

      <div className={`flex max-w-[82%] flex-col gap-2 ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Tool chips */}
        {message.toolEvents.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {message.toolEvents.map((label, i) => (
              <span
                key={`${label}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--ink-muted)]"
              >
                <ToolIcon label={label} />
                {label}
              </span>
            ))}
          </div>
        )}

        {/* Bubble */}
        {(bubbleText || showThinking) && (
          <div
            className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              isUser
                ? 'rounded-tr-md bg-primary text-white shadow-[var(--shadow-soft)]'
                : 'rounded-tl-md border border-[color:var(--surface-line)] bg-[var(--surface-panel)] text-[color:var(--ink-base)]'
            }`}
          >
            {showThinking ? (
              <span className="flex items-center gap-2 text-[color:var(--ink-muted)]">
                <ThinkingDots />
                <span className="text-xs">Analiza se gândește…</span>
              </span>
            ) : (
              <>
                <p className={`whitespace-pre-wrap ${isUser ? 'text-white' : ''}`}>{bubbleText}</p>
                {isStreaming && !isUser && (
                  <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-primary align-text-bottom" />
                )}
              </>
            )}
          </div>
        )}

        {/* Spec card */}
        {spec && <SpecCard spec={spec} />}

        {/* Timestamp */}
        {message.content && !message.pending && (
          <span className="px-1 text-[10px] text-[color:var(--ink-soft)]">
            {timeAgo(message.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Suggestion chips (empty state) ──────────────────────────────────────────

const SUGGESTIONS = [
  'Vreau un export PDF al dashboard-ului',
  'Există deja notificări pe Teams?',
  'Cum funcționează calculul SLA?',
  'Vreau filtre avansate pe lista de tichete',
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function FeatureRequestChatPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: requestData, isLoading } = useFeatureRequest(id);
  const { messages, phase, toolLabel, error, busy, send, stop } = useFeatureRequestChat(id);
  const transition = useTransitionFeatureRequest();
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const nearBottomRef = useRef(true);
  const userName = useAuthStore((s) => s.user?.name);

  const request = requestData?.data;
  const locked = request ? request.status !== 'IN_DISCUSSION' : false;
  const userInitial = (userName?.trim().charAt(0) || 'U').toUpperCase();

  // The submit button appears only when the AI has emitted a spec (it decides
  // when the spec is ready); the user decides when to press it.
  const specReady = useMemo(
    () => messages.some((m) => m.role === 'assistant' && extractSpec(m.content) !== null),
    [messages],
  );

  const handleSubmitForApproval = useCallback(() => {
    if (!request) return;
    transition.mutate(
      { id: request.id, action: 'submit_for_approval' },
      {
        onSuccess: () => toast('success', 'Sugestia a fost trimisă spre aprobare.'),
        onError: (err) => toast('error', err instanceof Error ? err.message : 'Nu am putut trimite sugestia spre aprobare.'),
      },
    );
  }, [request, transition, toast]);

  // Auto-scroll when new content arrives (only if the user is near the bottom).
  const lastMessage = messages[messages.length - 1];
  const contentLength = lastMessage ? lastMessage.content.length + (lastMessage.reasoning?.length ?? 0) : 0;
  // Single trigger value so the effect re-runs as the stream grows
  // (new message, new tokens, phase/tool changes).
  const scrollTrigger = `${messages.length}:${contentLength}:${phase}:${toolLabel ?? ''}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollTrigger is a derived re-run trigger, not read inside the effect
  useEffect(() => {
    const el = scrollRef.current;
    if (el && nearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [scrollTrigger]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  const submit = useCallback(() => {
    const value = input.trim();
    if (!value || busy || locked) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    nearBottomRef.current = true;
    void send(value);
  }, [input, busy, locked, send]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const statusBadge = useMemo(() => {
    if (!request) return null;
    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${FEATURE_REQUEST_STATUS_STYLES[request.status]}`}
      >
        {FEATURE_REQUEST_STATUS_LABELS[request.status]}
      </span>
    );
  }, [request]);

  if (isLoading) {
    return (
      <div className="mx-auto flex h-full max-w-3xl flex-col gap-4 p-6">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-4 py-3 md:px-6">
        <button
          type="button"
          onClick={() => navigate(request ? `/admin/feature-requests/${request.id}` : '/admin/feature-requests')}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[color:var(--ink-muted)] transition-colors hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-sm font-semibold text-[color:var(--ink-strong)]">
              {request?.title ?? 'Sugestie nouă'}
            </h1>
            {request?.type && (
              <span
                className={`hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold sm:inline ${FEATURE_REQUEST_TYPE_STYLES[request.type]}`}
              >
                {FEATURE_REQUEST_TYPE_LABELS[request.type].toUpperCase()}
              </span>
            )}
          </div>
          <p className="text-xs text-[color:var(--ink-muted)]">
            Conversație cu Analiza — asistentul de evaluare
          </p>
        </div>
        {statusBadge}
      </div>

      {/* Locked banner */}
      {locked && (
        <div className="flex items-center gap-2.5 border-b border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] px-6 py-2.5">
          <AlertTriangle size={14} className="shrink-0 text-[color:var(--ink-muted)]" />
          <p className="text-xs text-[color:var(--ink-muted)]">
            Conversația este încheiată — cererea a fost procesată.{' '}
            <button
              type="button"
              onClick={() => navigate(`/admin/feature-requests/${request?.id}`)}
              className="font-semibold text-primary hover:underline"
            >
              Vezi detaliile
            </button>
          </p>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 md:px-6">
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-5 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-white shadow-[var(--shadow-panel)]">
                <Sparkles size={26} />
              </span>
              <div>
                <h2 className="text-lg font-bold text-[color:var(--ink-strong)]">
                  Salut! Sunt Analiza.
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[color:var(--ink-muted)]">
                  Spune-mi ce vrei să schimbi, să adaugi sau ce nu merge. Te voi întreba critic,
                  voi verifica în cod și în date dacă ideea există deja, și la final îți voi
                  pregăti o specificație clară spre aprobare.
                </p>
              </div>
              <div className="flex max-w-lg flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      nearBottomRef.current = true;
                      void send(s);
                    }}
                    className="rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-4 py-2 text-xs font-medium text-[color:var(--ink-base)] transition-all hover:-translate-y-px hover:border-[color:var(--surface-line-strong)] hover:text-[color:var(--ink-strong)]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              isStreaming={busy && i === messages.length - 1 && m.role === 'assistant'}
              userInitial={userInitial}
            />
          ))}

          {/* Active tool status */}
          {phase === 'tool' && toolLabel && (
            <div className="flex gap-3">
              <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-white">
                <Sparkles size={14} />
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-3.5 py-2 text-xs font-medium text-[color:var(--ink-base)]">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                {toolLabel}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 rounded-2xl border border-error/25 bg-error/6 px-4 py-3">
              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-error" />
              <div>
                <p className="text-sm font-medium text-error">{error}</p>
                <p className="mt-0.5 text-xs text-[color:var(--ink-muted)]">
                  Încearcă din nou sau verifică conectivitatea.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-4 py-3.5 md:px-6">
        <div className="mx-auto max-w-3xl">
          {!locked && specReady && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-success/25 bg-success/6 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <CheckCircle2 size={16} className="shrink-0 text-success" />
                <p className="truncate text-xs text-[color:var(--ink-base)]">
                  Specificația este gata — poți trimite sugestia spre aprobare.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSubmitForApproval}
                disabled={transition.isPending}
                className="shrink-0 rounded-full bg-success px-4 py-2 text-xs font-bold text-white shadow-[var(--shadow-soft)] transition-all hover:-translate-y-px hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
              >
                {transition.isPending ? 'Se trimite…' : 'Trimite spre aprobare'}
              </button>
            </div>
          )}
          {locked ? (
            <p className="py-2 text-center text-xs text-[color:var(--ink-soft)]">
              Conversația este încheiată.
            </p>
          ) : (
            <div className="flex items-end gap-2.5">
              <div className="flex flex-1 items-end rounded-[1.4rem] border border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] px-4 py-2.5 transition-colors focus-within:border-[color:var(--surface-line-strong)] focus-within:ring-2 focus-within:ring-[color:var(--surface-focus)]">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    autoResize(e.target);
                  }}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder="Descrie ideea, modificarea sau bug-ul…"
                  aria-label="Scrie mesajul către asistentul AI"
                  className="max-h-40 flex-1 resize-none bg-transparent text-sm text-[color:var(--ink-strong)] placeholder:text-[color:var(--ink-soft)] focus:outline-none"
                />
              </div>
              {busy ? (
                <button
                  type="button"
                  onClick={stop}
                  title="Oprește generarea"
                  aria-label="Oprește generarea"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-[var(--shadow-soft)] transition-all hover:bg-primary-light"
                >
                  <Square size={15} fill="currentColor" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!input.trim()}
                  title="Trimite"
                  aria-label="Trimite mesajul"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-[var(--shadow-soft)] transition-all hover:-translate-y-px hover:bg-primary-light disabled:pointer-events-none disabled:opacity-40"
                >
                  <ArrowUp size={18} />
                </button>
              )}
            </div>
          )}
          <p className="mt-2 text-center text-[10px] text-[color:var(--ink-soft)]">
            Enter trimite · Shift+Enter linie nouă · AI-ul verifică ideea în cod și în date
          </p>
        </div>
      </div>
    </div>
  );
}
